// LoopbackBusSocket — the transport that makes a player's own machine its
// server. It implements the BusSocket surface BusConnection drives, but
// instead of a WebSocket to a remote server it bridges, in-process, to a
// LocalServer hosting the launched set's server halves (local-server.ts).
//
// This is the seam that lets the whole shared-bus stack run unchanged for
// singleplayer: BusConnection builds its transport through an injectable
// BusSocketFactory (bus-connection.ts), so the supervisor hands it a factory
// that returns one of these for the local bus. Riders, the bus, and the
// tenants are none the wiser — they speak the same framed-bytes protocol, it
// just loops back through localhost.
//
//   client → server : BusConnection.send(frame) → socket.send → feedFrom(0)
//   server → client : LocalServer sink → socket "message" → BusConnection
//                     broadcasts to every rider (each demuxes by channel)
//
// One loopback socket == one local player == slot 0 on the server.

import { encodeControlMessage } from "@multinostalgia/munos";
import type { BusSocket } from "./bus-connection.js";
import type { LocalServer } from "./local-server.js";
import type { HostedClient } from "./peer-host.js";

// WebSocket.readyState values, named locally so this needs no DOM global.
const OPEN = 1;
const CLOSED = 3;

// The default single-player slot. The unmanaged loopback is inherently one
// connection on slot 0; a managed loopback (split-screen) gets an allocated slot.
const LOCAL_SLOT = 0;

type Listener = (ev: { data?: unknown }) => void;

export interface LoopbackBusSocketOptions {
  // When false, the loopback does NOT claim the LocalServer's single sink. In
  // P2P host mode the server also serves remote peers, and PeerHost owns the
  // sink as the one slot-aware router — it feeds this local client (slot 0) via
  // deliver()/deliverPeerLeft(). Default true: singleplayer, where the loopback
  // is the only client and owns the sink outright.
  ownSink?: boolean;
  // MANAGED mode (split-screen: several local tiles share one LocalServer). When
  // given, a router — PeerHost — owns the server sink and allocates this
  // socket's slot: on open the socket calls `attach(self)` (which registers it
  // as the slot's HostedClient and connects it, returning the slot, or -1 if the
  // server is full), and on close it calls `detach(slot)` to free it. The socket
  // then rides that allocated slot instead of the hardcoded slot 0, so N local
  // players coexist. Supersedes ownSink (a managed socket never claims the sink).
  attach?: (self: LoopbackBusSocket) => number;
  detach?: (slot: number) => void;
}

export class LoopbackBusSocket implements BusSocket, HostedClient {
  readyState = OPEN;
  binaryType = "arraybuffer";
  private readonly listeners = new Map<string, Listener[]>();
  // This connection's slot on the server. Fixed at LOCAL_SLOT for the
  // unmanaged (single-player / host-self-play) paths; assigned by the router in
  // managed mode (split-screen). -1 until open when managed, so a close before
  // the slot is allocated is a no-op.
  private slot: number;
  private readonly attach: ((self: LoopbackBusSocket) => number) | null;
  private readonly detach: ((slot: number) => void) | null;

  constructor(private readonly server: LocalServer, opts: LoopbackBusSocketOptions = {}) {
    this.attach = opts.attach ?? null;
    this.detach = opts.detach ?? null;
    const managed = this.attach !== null;
    this.slot = managed ? -1 : LOCAL_SLOT;
    // Wire the server's outbound: a server→client frame becomes a binary
    // "message"; an interest leave becomes a peer_left control (text) frame —
    // exactly the two shapes BusConnection's message handler distinguishes.
    // In host / split-screen mode PeerHost owns the sink instead and calls
    // deliver()/deliverPeerLeft() for this socket's slot, so we skip claiming it.
    if (!managed && opts.ownSink !== false) {
      this.server.setSink(
        (_toSlot, bytes) => this.deliver(bytes),
        (_toSlot, slot) => this.deliverPeerLeft(slot),
      );
    }
    // Open + connect the local player on a microtask: BusConnection registers
    // its "open"/"message" listeners synchronously right after constructing the
    // socket, so deferring one microtask guarantees they're attached first.
    queueMicrotask(() => {
      if (this.readyState !== OPEN) return;
      if (managed) {
        // The router allocates + connects this socket's slot (registering it as
        // the slot's HostedClient first, so a connect-time send already routes).
        const s = this.attach(this);
        if (s < 0) { this.close(); return; } // server full — fail the open cleanly
        this.slot = s;
      } else {
        this.server.connectSlot(this.slot, new Uint8Array(0));
      }
      this.emit("open", {});
    });
  }

  // Deliver a server→client frame to this local client (HostedClient). Public so
  // the shared sink router (PeerHost, in host / split-screen mode) can feed this
  // tile's slot; the self-owned singleplayer path above routes through it too.
  // `reliable` is ignored — the loopback has no wire, so both classes deliver the
  // same way (the parameter exists only to satisfy HostedClient).
  deliver(bytes: Uint8Array, _reliable?: boolean): void {
    if (this.readyState === OPEN) this.emit("message", { data: toArrayBuffer(bytes) });
  }

  // Deliver an interest-management leave as a peer_left control frame. `subject`
  // is the slot that left this client's interest set.
  deliverPeerLeft(subject: number): void {
    if (this.readyState === OPEN) {
      this.emit("message", { data: encodeControlMessage({ type: "peer_left", slot: subject }) });
    }
  }

  send(data: Uint8Array): void {
    if (this.readyState !== OPEN || this.slot < 0) return;
    this.server.feedFrom(this.slot, data);
  }

  close(): void {
    if (this.readyState === CLOSED) return;
    this.readyState = CLOSED;
    // Free the slot: managed sockets go through the router (which drops them
    // from its routing table too), the rest disconnect directly. A managed
    // socket closed before its slot was allocated (slot < 0) has nothing to free.
    if (this.slot >= 0) {
      if (this.detach) this.detach(this.slot);
      else this.server.disconnectSlot(this.slot);
    }
    this.emit("close", {});
  }

  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: Listener,
  ): void {
    const arr = this.listeners.get(type);
    if (arr) arr.push(listener);
    else this.listeners.set(type, [listener]);
  }

  private emit(type: string, ev: { data?: unknown }): void {
    const arr = this.listeners.get(type);
    if (!arr) return;
    // Snapshot — a handler may mutate the rider set / close the socket.
    for (const l of [...arr]) l(ev);
  }
}

// Hand BusConnection a real ArrayBuffer (its message handler checks
// `data instanceof ArrayBuffer`). A wasm-heap subarray is a view over a larger
// buffer, so slice out exactly this frame's bytes into a standalone buffer.
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
